/**
 * ONE ASK, ONE TOTAL (2026-10-24, docs/PLAN-usage-metering-20261017.md step 12): the parts one ask is billed in, and
 * the one count of its steps.
 *
 * Malaika, 2026-10-23: a long ask with tasks at once came back saying "35 steps" and "less than one pr0" while its card
 * had read "step 120" — "these tracking of total steps and pr0 work units need to be very accurate on the next message
 * that has an i information icon … and on the activity page and condenses as is conventional on the wallet page". One
 * ask is billed as several Activity lines and Wallet rows: the turn that was asked, each hand-on that carried the work
 * on, each task at once, each background task it started. The ⓘ read one of them. Every debit now names its ask (the
 * ask's root task, askTree.ts) and which part of it it is, so the ⓘ, the Activity page and the Wallet add up the same
 * lines (functions/src/billing/askUsage.ts reads them).
 *
 * STEPS (her ruling, 2026-10-24: "One total, tasks included"). The live card's "step N" and the ⓘ count the same thing:
 * the main work's steps and every task at once's, never the card's opening row. So the last number the card showed is
 * the number the ⓘ shows, and each task's row shows its own.
 *
 * VERBATIM TWINS: functions/src/lib/askTotals.ts and src/config/askTotals.ts are BYTE-IDENTICAL, pinned by
 * tests/activity/askTotals.parity.test.ts. Nothing here imports anything.
 */

/** Every part an ask's debits can be, in the order the ⓘ and the Activity page list them. */
export const ASK_PARTS = ['ask', 'hop', 'branch', 'delegated', 'note'] as const;
export type AskPart = (typeof ASK_PARTS)[number];

export function isAskPart(v: unknown): v is AskPart {
  return typeof v === 'string' && (ASK_PARTS as readonly string[]).includes(v);
}

/** An ask's id as it is stamped: its root task's id, never a path. */
const ASK_TASK_ID_RE = /^[A-Za-z0-9_-]{1,160}$/;

/** PURE. A stamped ask id, or null for anything else. */
export function askTaskIdOrNull(v: unknown): string | null {
  const s = typeof v === 'string' ? v.trim() : '';
  return ASK_TASK_ID_RE.test(s) ? s : null;
}

/** What a debit says of its ask: the ask's root task and which part of it the debit is. */
export interface AskStamp {
  taskId: string;
  part: AskPart;
}

/** PURE. A stamp read closed: both fields well formed, or null. */
export function askStampOf(ask: { taskId?: unknown; part?: unknown } | null | undefined): AskStamp | null {
  const taskId = askTaskIdOrNull(ask?.taskId);
  const part = ask?.part;
  return taskId && isAskPart(part) ? { taskId, part } : null;
}

/**
 * PURE. Which part of its ask a turn is, from the turn's own facts: the root's own turn is the ask; a task at once is
 * a branch; a note's turn a note; a task the primary delegated (one level below the root) is background work; any other
 * task of the ask carries it on (a computer hand-on, a long document's or a batch's next envelope, a retry). Null for a
 * turn with no task behind it (it is its own ask, alone).
 */
export function askStampOfTurn(t: {
  taskId?: string | null;
  askTaskId?: string | null;
  branch?: boolean;
  note?: boolean;
  askDepth?: number | null;
}): AskStamp | null {
  const taskId = askTaskIdOrNull(t.taskId);
  const askTaskId = askTaskIdOrNull(t.askTaskId) || taskId;
  if (!askTaskId) return null;
  if (taskId === askTaskId) return { taskId: askTaskId, part: 'ask' };
  if (t.branch === true) return { taskId: askTaskId, part: 'branch' };
  if (t.note === true) return { taskId: askTaskId, part: 'note' };
  const depth = typeof t.askDepth === 'number' && Number.isFinite(t.askDepth) ? t.askDepth : 0;
  return { taskId: askTaskId, part: depth >= 1 ? 'delegated' : 'hop' };
}

/**
 * PURE. The words a part is named by on the ⓘ and the Activity page, for how many lines it is: state words on a billing
 * row (D9 `usage_receipt`), never a sentence in Prest0n's voice.
 */
export function askPartLabel(part: AskPart, lines: number): string {
  // copy: usage_receipt
  const n = Math.max(1, Math.floor(Number.isFinite(lines) ? lines : 1));
  if (part === 'ask') return 'starting the work';
  if (part === 'hop') return n > 1 ? `carrying the work on · ${n} times` : 'carrying the work on';
  if (part === 'branch') return n > 1 ? `${n} tasks at once` : 'a task at once';
  if (part === 'delegated') return n > 1 ? `${n} background tasks` : 'a background task';
  return n > 1 ? `${n} notes while working` : 'a note while working';
}

function whole(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** PURE. The steps a task at once took: its own count (it has no opening row). */
export function branchWorkSteps(branch: { stepCount?: unknown } | null | undefined): number {
  return whole(branch?.stepCount);
}

/**
 * PURE. The steps one ask's card stands for: the main work's (its count less the opening row every card starts with)
 * and every task at once's. Reads the server's live doc (`branches` a map by id) and the client's parsed card
 * (`branches` an array) alike.
 */
export function askWorkSteps(card: { stepCount?: unknown; branches?: unknown } | null | undefined): number {
  const own = Math.max(0, whole(card?.stepCount) - 1);
  const raw = card?.branches;
  const branches: unknown[] = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw as object) : [];
  return branches.reduce<number>((sum, b) => sum + branchWorkSteps(b as { stepCount?: unknown }), own);
}
